import { rHandler } from '@/plugins/RHandler';
import BaseModel from '../../../../base';

import constants from './constants';
import functions from './functions';
import glossary from './glossary';

import TournamentStageGroupMemberHistory from './history/index';
import { apiService } from '@/services/APIService';
import { DEFAULT_PARAMS } from '@/utils/objectsRHandler';
import Task from '@/models/list/task/index';
import TournamentStageGroupUserScore from './scores';

export default class TournamentStageGroupMember extends BaseModel {
  constructor(member = {}, url) {
    super(member, url);

    this.avatar = member.avatar;
    this.full_name = member.full_name;
    this.score = member.score;
    this.tournament_stage_group_user_assignment_id = member.tournament_stage_group_user_assignment_id;
    const additionalScores = (
      member.dop_scores
      || member.user_scores
      || member.additional_scores
      || []
    );
    this.additional_scores = Array.isArray(additionalScores)
      ? additionalScores.map((score) => new TournamentStageGroupUserScore(score))
      : [];
    this.username = member.username;
    this.status = member.status || constants.TOURNAMENT_MEMBER_STATUS.waited;

    this.description = member.description || '';
    this.code = member.code || '';

    this.is_transferred = member.is_transferred ?? false;

    this.prize_place = member.prize_place ?? null;

    this.history = (member.history || [])
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((item) => new TournamentStageGroupMemberHistory(item));

    this.polls = (member.polls || []).map((poll) => new Task({
      ...poll,
      poll_journals: [poll.poll_journal].filter(Boolean),
    }));

    this.final_result = member.final_result || 0;

    this.poll = member.poll ? new Task({
      ...member.poll,
      poll_journals: [member.poll.poll_journal].filter(Boolean),
    }) : undefined;
  }

  get displayName() {
    const { code, name } = this;
    return [
      name,
      ...code ? [`(${code})`] : [],
    ].join(' ');
  }

  get lastHistory() {
    const { TOURNAMENT_MEMBER_STATUS: { transfered } } = constants;
    return this.history.filter((i) => i.status !== transfered).at(-1);
  }

  get actualStatus() {
    const { is_transferred, status } = this;
    return is_transferred ? constants.TOURNAMENT_MEMBER_STATUS.levelTransfered : status;
  }

  get totalScore() {
    return [
      Number(this.score || 0),
      ...(this.polls || []).map((i) => i?.lastPollJournal?.actualScore || 0),
    ].reduce((summ, score) => summ + score, 0);
  }

  get additionalScores() {
    return TournamentStageGroupUserScore.toValues(this.additional_scores);
  }

  update(rParams = DEFAULT_PARAMS) {
    const { levelTransfered, ended } = constants.TOURNAMENT_MEMBER_STATUS;
    const { data, obs } = rParams;
    const { lastHistory, score, description, status, actualStatus, code, prize_place } = this;
    const currentData = {
      score,
      description,
      status: actualStatus === levelTransfered ? ended : status,
      prize_place,
      code,
      ...data,
    };
    const payload = {
      users: [
        {
          user_id: this.id,
          ...currentData,
        },
      ],
    };
    return rHandler(
      apiService.post(`tournament-stage-groups/${lastHistory?.tournament_stage_group_id}/users/score`, payload),
      (response) => {
        Object.assign(this, currentData);
        return { response };
      },
      (error) => ({ error }),
      obs,
    );
  }

  finish() {
    return this.update({
      data: {
        status: constants.TOURNAMENT_MEMBER_STATUS.ended,
      },
    });
  }

  resume() {
    return this.update({
      data: {
        status: constants.TOURNAMENT_MEMBER_STATUS.waited,
      },
    });
  }

  get isTransfered() {
    const { TOURNAMENT_MEMBER_STATUS: { transfered, levelTransfered } } = constants;
    const { actualStatus } = this;
    return [transfered, levelTransfered].includes(actualStatus);
  }

  getPrizePlace(award_settings = {}) {
    const { prize_place } = this;
    return functions.getPrizePlaceByAwards(prize_place, award_settings);
  }

  statusData(award_settings = {}) {
    const { TOURNAMENT_MEMBER_STATUS: { transfered, ended } } = constants;
    const { TOURNAMENT_MEMBER_STATUS_DATA } = glossary;
    const { actualStatus: status } = this;
    const data = TOURNAMENT_MEMBER_STATUS_DATA[status];
    if (status === transfered) {
      const { lastHistory } = this;
      return {
        ...data,
        name: [
          'Переведён',
          ...lastHistory ? [`в ${lastHistory.tournament_stage.name} (${lastHistory.tournament_stage_group.name})`] : [],
        ].join(' '),
      };
    }
    const prizePlace = this.getPrizePlace(award_settings);
    if ((status === ended) && prizePlace && (prizePlace !== constants.TOURNAMENT_MEMBER_PRIZE_PLACE.member)) {
      return {
        ...data,
        name: glossary.TOURNAMENT_MEMBER_PRIZE_PLACE_DATA[prizePlace]?.name || data?.name,
      };
    }
    return data;
  }

  // statusDataByGroup(groupId) {
  //   const { TOURNAMENT_MEMBER_STATUS: { transfered } } = constants;
  //   const { TOURNAMENT_MEMBER_STATUS_DATA } = glossary;
  //   const { status } = this;
  //   const data = TOURNAMENT_MEMBER_STATUS_DATA[status];
  //   if (status === transfered) {
  //     const nextHistory = this.history.find(({ parent_tournament_stage_group_id: id }) => id === groupId);
  //     return {
  //       ...data,
  //       name: [
  //         'Переведён',
  //         ...nextHistory ? [`в ${nextHistory.tournament_stage.name} (${nextHistory.tournament_stage_group.name})`] : [],
  //       ].join(' '),
  //     };
  //   }
  //   return data;
  // }

  get actions() {
    return functions.getActionsByStatus(this.actualStatus);
  }

  static get constants() {
    return constants;
  }

  static get glossary() {
    return glossary;
  }

  static get functions() {
    return functions;
  }
}
